import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatMoney } from "@/lib/api";
import { CART } from "@/constants/testIds";

export default function Cart() {
  const { items, updateQty, removeItem, totals } = useCart();
  const nav = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="overline text-emerald-800 mb-3">Your cart</p>
        <h1 className="font-display text-3xl font-black text-slate-900">Nothing here yet.</h1>
        <p className="mt-3 text-slate-500">Add some supplies to get started.</p>
        <Link to="/shop" className="mt-8 inline-block px-6 py-3 bg-emerald-900 text-white text-sm rounded-sm hover:bg-emerald-800">
          Browse catalog
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
      <p className="overline text-emerald-800 mb-2">Cart</p>
      <h1 className="font-display text-4xl font-black text-slate-900 tracking-tight mb-10">
        Review your order
      </h1>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100 bg-white">
            {items.map((it) => (
              <li key={it.product_id} className="p-5 flex gap-4">
                <div className="w-24 h-24 bg-slate-50 border border-slate-200 rounded-sm overflow-hidden flex-shrink-0">
                  <img src={it.image_url} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${it.product_id}`} className="font-display font-medium text-slate-900 hover:text-emerald-900">
                    {it.name}
                  </Link>
                  <p className="text-xs text-slate-500 mt-0.5">SKU {it.sku}</p>
                  <div className="flex items-center gap-4 mt-4">
                    <div className="inline-flex items-center border border-slate-300 rounded-sm">
                      <button
                        data-testid={CART.qtyMinus(it.product_id)}
                        onClick={() => updateQty(it.product_id, it.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-slate-100"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center text-sm">{it.quantity}</span>
                      <button
                        data-testid={CART.qtyPlus(it.product_id)}
                        onClick={() => updateQty(it.product_id, it.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-slate-100"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button
                      data-testid={CART.remove(it.product_id)}
                      onClick={() => removeItem(it.product_id)}
                      className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold text-slate-900">
                    {formatMoney(it.price * it.quantity)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{formatMoney(it.price)} ea</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <div className="sticky top-24 bg-white border border-slate-200 rounded-sm p-6">
            <p className="overline text-emerald-800 mb-4">Summary</p>
            <div className="space-y-2 text-sm">
              <Row label="Subtotal" val={<span data-testid={CART.subtotal}>{formatMoney(totals.subtotal)}</span>} />
              <Row label="Shipping" val={totals.shipping === 0 ? "FREE" : formatMoney(totals.shipping)} />
              <Row label="Tax (8%)" val={formatMoney(totals.tax)} />
              <div className="flex justify-between pt-3 border-t border-slate-200 font-display font-bold text-slate-900 text-base">
                <span>Total</span>
                <span>{formatMoney(totals.total)}</span>
              </div>
            </div>
            <button
              data-testid={CART.checkoutBtn}
              onClick={() => nav("/checkout")}
              className="mt-6 w-full py-3.5 bg-emerald-900 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800"
            >
              Checkout
            </button>
            <p className="text-[11px] text-slate-500 mt-3 text-center">
              Distributor drop-ships direct — no-touch freight.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, val }) {
  return (
    <div className="flex justify-between text-slate-600">
      <span>{label}</span>
      <span className="text-slate-900">{val}</span>
    </div>
  );
}
